/**
 * «Куда положить»: выбор существующей библиотеки или создание новой.
 *
 * Сам ничего не кладёт — отдаёт выбранный id владельцу (`onPick`): что именно
 * кладут (пин, загруженный файл) решает экран. Библиотеки из `/api/libraries`
 * сгруппированы по категории, как на экране «Библиотеки».
 *
 * Последний выбор помнит владелец (`initialLibraryId`): пины разбирают пачкой
 * в одну и ту же библиотеку, и выбирать её каждый раз заново — мучение.
 */
import { useQuery } from "@tanstack/react-query";
import { FolderPlus, ImagePlus } from "lucide-react";
import { useState, type FormEvent } from "react";

import { LibraryCreateForm } from "@/components/library/LibraryCreateForm";
import { btn, btnPrimary, ErrorLine, input, label } from "@/components/trends/ui";
import { flatLibraries, libraryKeys, loadLibraries, LOOK_CATEGORY } from "@/lib/library";

export function LibraryPicker({
  initialLibraryId,
  defaultName,
  defaultCategory = LOOK_CATEGORY,
  busy = false,
  submitLabel = "В библиотеку",
  onPick,
}: {
  initialLibraryId?: number | null;
  /** Название новой библиотеки по умолчанию, например «Свадебные платья · кружевное». */
  defaultName: string;
  defaultCategory?: string;
  busy?: boolean;
  submitLabel?: string;
  onPick: (libraryId: number, libraryName: string) => void | Promise<void>;
}) {
  const list = useQuery({ queryKey: libraryKeys.list, queryFn: loadLibraries });
  const libs = flatLibraries(list.data);
  const [mode, setMode] = useState<"pick" | "new" | null>(null);
  const [picked, setPicked] = useState<number | null>(null);

  // Что предложить по умолчанию: последний выбор, библиотека с тем же именем,
  // первая из нужной категории. Подставлять библиотеку ЧУЖОЙ категории нельзя:
  // платье легло бы в «Интерьеры» одним невнимательным кликом. Предложить
  // нечего — сразу форма новой.
  const suggested =
    libs.find((l) => l.id === initialLibraryId)?.id ??
    libs.find((l) => l.name.trim().toLowerCase() === defaultName.trim().toLowerCase())?.id ??
    libs.find((l) => l.category_code === defaultCategory)?.id ??
    null;
  const effectiveMode = mode ?? (suggested === null ? "new" : "pick");
  const libraryId = picked !== null && libs.some((l) => l.id === picked) ? picked : (suggested ?? libs[0]?.id ?? null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const lib = libs.find((l) => l.id === libraryId);
    if (lib && !busy) void onPick(lib.id, lib.name);
  };

  if (list.isPending) return <div className="text-[12.5px] text-fg-faint">Загружаю библиотеки…</div>;
  if (list.isError) return <ErrorLine error={list.error} />;

  if (effectiveMode === "new")
    return (
      <div className="grid gap-2" data-annot="новая библиотека для референса">
        <div className="text-[11.5px] text-fg-faint">Новая библиотека — пин ляжет в неё сразу после создания.</div>
        <LibraryCreateForm
          compact
          defaultName={defaultName}
          defaultCategory={defaultCategory}
          submitLabel="Создать и положить"
          onCreated={async (id, name) => {
            await onPick(id, name);
            // Новая библиотека становится выбранной: следующий пин — туда же, без формы.
            setPicked(id);
            setMode("pick");
          }}
          {...(libs.length ? { onCancel: () => setMode("pick") } : {})}
        />
      </div>
    );

  return (
    <form onSubmit={submit} className="grid gap-2" data-annot="выбор библиотеки">
      <label className="grid gap-1">
        <span className={label}>библиотека</span>
        <select
          className={`${input} w-full`}
          value={libraryId ?? ""}
          onChange={(e) => setPicked(Number(e.target.value))}
        >
          {list.data.groups.map((g) => (
            <optgroup key={g.code} label={g.name}>
              {g.libs.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} · {l.photos} фото
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={btnPrimary} disabled={libraryId === null || busy}>
          <ImagePlus size={14} />
          {busy ? "Кладу…" : submitLabel}
        </button>
        <button type="button" className={btn} onClick={() => setMode("new")}>
          <FolderPlus size={13} />
          Новая библиотека
        </button>
      </div>
    </form>
  );
}
